import {Color} from '@benchling/nucleus-ui';
import {Text, TextInput} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import notifications from 'notifications';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import CollapsibleTransitionCard from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/CollapsibleTransitionCard';
import {notifySchemaUpdateError} from 'processes/settings/ProcessTaskSchemaUtils';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {findDuplicateField, massageFieldsForSubmit, notifyDuplicateField} from 'schemas/SchemaUtil';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {BLUE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';

export const NO_CONFIGURED_FIELDS_MESSAGE = 'No input or output fields';

// Registers all left-column cells across every table/accordion and computes the
// max natural width so every table can align its first column identically.
interface ColumnWidthContextType {
  registerCell: (el: HTMLElement | null) => void;
  leftColWidth: number | undefined;
}

const ColumnWidthContext = createContext<ColumnWidthContextType>({
  registerCell: () => {},
  leftColWidth: undefined,
});

/**
 * Used to measure and determine the width of the first column in the system name tables.
 * Leverages layout effects to measure the max width and modify styling in a way the user
 * doesn't notice the change.
 */
function ColumnWidthProvider({children}: {children: React.ReactNode}): JSX.Element {
  const cellRefs = useRef(new Set<HTMLElement>());
  const [leftColWidth, setLeftColWidth] = useState<number | undefined>(undefined);
  const [cellCount, setCellCount] = useState(0);

  const registerCell = useCallback((el: HTMLElement | null) => {
    if (el) {
      const prevSize = cellRefs.current.size;
      cellRefs.current.add(el);
      if (cellRefs.current.size !== prevSize) {
        setCellCount(cellRefs.current.size);
      }
    }
  }, []);

  useLayoutEffect(() => {
    // Remove any disconnected cells from the set due to unmount, etc
    for (const el of cellRefs.current) {
      if (!el.isConnected) {
        cellRefs.current.delete(el);
      }
    }

    const spans = Array.from(cellRefs.current);
    if (spans.length === 0) {
      return;
    }

    // Temporarily prevent wrapping so we measure natural text width,
    // even for spans already constrained by a fixed-width column.
    const savedWhiteSpace = spans.map((span) => {
      const saved = span.style.whiteSpace;
      span.style.whiteSpace = 'nowrap';
      return saved;
    });

    let max = 0;
    spans.forEach((span) => {
      const w = span.getBoundingClientRect().width;
      if (w > max) {
        max = w;
      }
    });

    // Restore whitespace after initial measurement
    spans.forEach((span, i) => {
      span.style.whiteSpace = savedWhiteSpace[i];
    });

    if (max > 0) {
      // Font rendering has variance during layout, add buffer to ensure we don't underestimate the width
      // and cause text wrapping before MAX_FIRST_COL_WIDTH is reached.
      const FONT_RENDERING_BUFFER = 8;
      const CELL_HORIZONTAL_PADDING = 16;
      const COL_GAP = 24;
      const MAX_FIRST_COL_WIDTH = 450;
      setLeftColWidth(
        Math.min(
          Math.ceil(max) + CELL_HORIZONTAL_PADDING + COL_GAP + FONT_RENDERING_BUFFER,
          MAX_FIRST_COL_WIDTH
        )
      );
    }
  }, [cellCount]);

  const value = useMemo(() => ({registerCell, leftColWidth}), [registerCell, leftColWidth]);

  return <ColumnWidthContext.Provider value={value}>{children}</ColumnWidthContext.Provider>;
}

interface SchemaFieldInfo {
  name: string;
  systemName: string;
  isArchived: boolean;
  archivePurpose: string | null;
}

type FieldCategory = 'input' | 'output';

interface SchemaSection {
  id: string;
  name: string;
  inputFields: Array<SchemaFieldInfo>;
  outputFields: Array<SchemaFieldInfo>;
  allInputFieldsJson: Array<SchemaField>;
  allOutputFieldsJson: Array<SchemaField>;
}

function parseFields(fieldsJson: unknown): Array<SchemaFieldInfo> {
  if (!Array.isArray(fieldsJson)) {
    return [];
  }
  return fieldsJson
    .filter(
      (f): f is {name: string; systemName?: string; archiveRecord?: {purpose?: string} | null} =>
        f != null && typeof f.name === 'string'
    )
    .map((f) => ({
      name: f.name,
      systemName: f.systemName ?? '',
      isArchived: f.archiveRecord != null,
      archivePurpose:
        f.archiveRecord != null && typeof f.archiveRecord.purpose === 'string'
          ? f.archiveRecord.purpose
          : null,
    }));
}

function parseFieldsJson(fieldsJson: unknown): Array<SchemaField> {
  if (!Array.isArray(fieldsJson)) {
    return [];
  }
  return fieldsJson.filter((field): field is SchemaField => isSchemaField(field));
}

function isSchemaField(field: unknown): field is SchemaField {
  return isObjectRecord(field) && typeof field.name === 'string' && typeof field.systemName === 'string';
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null;
}

function buildSchemaSections(
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL,
  availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>
): Array<SchemaSection> {
  const seenIds = new Set<string>();
  const sections: Array<SchemaSection> = [];

  const addSchemaSection = (schema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL): void => {
    if (seenIds.has(schema.id)) {
      return;
    }
    seenIds.add(schema.id);
    sections.push({
      id: schema.id,
      name: schema.name,
      inputFields: parseFields(schema.allFields),
      outputFields: schema.outputSchema != null ? parseFields(schema.outputSchema.allFields) : [],
      allInputFieldsJson: parseFieldsJson(schema.allFields),
      allOutputFieldsJson: schema.outputSchema != null ? parseFieldsJson(schema.outputSchema.allFields) : [],
    });
  };

  const sortedSchemas = availableTaskSchemas
    .filter((s) => s.id !== rootTaskSchema.id)
    .toSorted((a, b) => a.name.localeCompare(b.name));

  addSchemaSection(rootTaskSchema);
  for (const schema of sortedSchemas) {
    addSchemaSection(schema);
  }

  return sections;
}

interface EditingState {
  fieldName: string;
  value: string;
}

function FieldsTable(props: {
  fields: Array<SchemaFieldInfo>;
  headerLabel: string;
  dataTestComponent: string;
  onSystemNameSave?: (fieldName: string, newSystemName: string) => Promise<void>;
}): JSX.Element | null {
  const {fields, headerLabel, dataTestComponent, onSystemNameSave} = props;
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [saving, setSaving] = useState(false);
  const {registerCell, leftColWidth} = useContext(ColumnWidthContext);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const activeFields = useMemo(() => fields.filter((f) => !f.isArchived), [fields]);
  const archivedFields = useMemo(() => fields.filter((f) => f.isArchived), [fields]);

  useLayoutEffect(() => {
    if (leftColWidth == null || !wrapperRef.current) {
      return;
    }
    const headerCells = wrapperRef.current.querySelectorAll('thead th');
    (headerCells[0] as HTMLElement).style.setProperty('width', `${leftColWidth}px`, 'important');
    (headerCells[1] as HTMLElement).style.setProperty('width', 'auto', 'important');
  }, [leftColWidth]);

  const handleSave = useCallback(async () => {
    if (editing == null || onSystemNameSave == null) {
      setEditing(null);
      return;
    }
    const originalField = fields.find((f) => f.name === editing.fieldName);
    if (originalField == null || editing.value === originalField.systemName) {
      setEditing(null);
      return;
    }
    setSaving(true);
    try {
      await onSystemNameSave(editing.fieldName, editing.value);
    } finally {
      setSaving(false);
      setEditing(null);
    }
  }, [editing, fields, onSystemNameSave]);

  const handleCancel = useCallback(() => {
    setEditing(null);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave().catch(() => undefined);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  if (fields.length === 0) {
    return null;
  }

  const visibleFields = showArchived ? [...activeFields, ...archivedFields] : activeFields;
  const archivedStartIndex = activeFields.length;
  const hasArchivedToggleRow = !showArchived && archivedFields.length > 0;
  const totalRows = visibleFields.length + (hasArchivedToggleRow ? 1 : 0);

  return (
    <div ref={wrapperRef} className={css(styles.tableWrapper)}>
      <Table
        numRows={totalRows}
        calculateInView={false}
        fitContent={false}
        frozenHeader={true}
        getRowKey={(rowIndex) =>
          rowIndex === visibleFields.length ? '__show_archived__' : visibleFields[rowIndex].name
        }
        includeNegativeMargins={false}
        isCompact={true}
        overflowVisible={true}
        tableLayout="fixed"
      >
        <Column
          header={
            <Cell className={css(styles.headerCell)}>
              <span ref={registerCell}>{headerLabel}</span>
            </Cell>
          }
          renderCell={(rowIdx: number) => {
            if (rowIdx === visibleFields.length) {
              return (
                <Cell>
                  <a
                    href=""
                    className={css(styles.showArchivedLink)}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowArchived(true);
                    }}
                    {...getTestProps({component: dataTestComponent, element: 'showArchived'})}
                  >
                    Show archived fields ({archivedFields.length})
                  </a>
                </Cell>
              );
            }
            const field = visibleFields[rowIdx];
            return (
              <Cell
                className={css(
                  styles.fieldNameCell,
                  showArchived && rowIdx >= archivedStartIndex && styles.archivedField
                )}
              >
                <span ref={registerCell}>
                  {field.name}
                  {field.archivePurpose != null && ` (${field.archivePurpose})`}
                </span>
              </Cell>
            );
          }}
        />
        <Column
          header={<Cell className={css(styles.headerCell)}>System Name</Cell>}
          renderCell={(rowIdx: number) => {
            if (rowIdx === visibleFields.length) {
              return <Cell />;
            }
            const field = visibleFields[rowIdx];
            const isEditing = editing?.fieldName === field.name;

            if (isEditing) {
              return (
                <Cell className={css(styles.systemNameCell)}>
                  <div className={css(styles.editingContainer)}>
                    <TextInput
                      size="xs"
                      value={editing.value}
                      onChange={(e) => {
                        setEditing({...editing, value: e.currentTarget.value});
                      }}
                      onKeyDown={handleKeyDown}
                      onBlur={() => {
                        handleSave().catch(() => undefined);
                      }}
                      disabled={saving}
                      autoFocus={true}
                      styles={(theme) => ({
                        input: {
                          fontFamily: FONT_FAMILY_MONOSPACED,
                          fontSize: theme.fontSizes.xs,
                        },
                      })}
                      {...getTestProps({
                        component: dataTestComponent,
                        element: 'editSystemNameInput',
                      })}
                    />
                  </div>
                </Cell>
              );
            }

            return (
              <Cell
                className={`clipboardIcon-container ${css(
                  styles.systemNameCell,
                  showArchived && rowIdx >= archivedStartIndex && styles.archivedField
                )}`}
              >
                <span className={css(styles.systemNameText)}>{field.systemName}</span>
                {onSystemNameSave != null && !field.isArchived && (
                  <span
                    className={`clipboardIcon ${css(styles.actionIcon)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing({fieldName: field.name, value: field.systemName});
                    }}
                    {...getTestProps({
                      component: dataTestComponent,
                      element: 'editSystemName',
                    })}
                  >
                    <BenchlingIcon icon="edit" />
                  </span>
                )}
                <span
                  className={`clipboardIcon ${css(styles.actionIcon)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(field.systemName, {showToastOnSuccess: true});
                  }}
                  {...getTestProps({component: dataTestComponent, element: 'copySystemName'})}
                >
                  <BenchlingIcon icon="copy" />
                </span>
              </Cell>
            );
          }}
        />
      </Table>
    </div>
  );
}

async function saveSystemName(
  schemaId: string,
  fieldName: string,
  newSystemName: string,
  allFieldsJson: Array<SchemaField>,
  category: FieldCategory
): Promise<void> {
  const updatedFields = allFieldsJson.map((f) =>
    f.name === fieldName ? {...f, systemName: newSystemName} : f
  );
  const massaged = massageFieldsForSubmit(updatedFields);

  if (category === 'input') {
    await ProcessSchemaDAO.updateProcessTaskSchema({
      schemaId,
      schemaFields: massaged,
      processOutputSchema: {},
    });
  } else {
    await ProcessSchemaDAO.updateProcessTaskSchema({
      schemaId,
      processOutputSchema: {
        schemaFields: massaged,
      },
    });
  }
}

export interface FlowchartSystemNamesTabProps {
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
}

export default function FlowchartSystemNamesTab(props: FlowchartSystemNamesTabProps): JSX.Element {
  const {rootTaskSchema, availableTaskSchemas} = props;

  const schemaSections = useMemo(
    () => buildSchemaSections(rootTaskSchema, availableTaskSchemas),
    [rootTaskSchema, availableTaskSchemas]
  );
  const [sections, setSections] = useState(schemaSections);

  useEffect(() => {
    setSections(schemaSections);
  }, [schemaSections]);

  const handleSystemNameSave = useCallback(
    (sectionId: string, category: FieldCategory) =>
      async (fieldName: string, newSystemName: string): Promise<void> => {
        const currentSections = sectionsRef.current;
        const section = currentSections.find((s) => s.id === sectionId);
        if (section == null) {
          return;
        }
        const allFieldsJson = category === 'input' ? section.allInputFieldsJson : section.allOutputFieldsJson;
        const updatedFields = allFieldsJson.map((f) =>
          f.name === fieldName ? {...f, systemName: newSystemName} : f
        );
        const duplicate = findDuplicateField(updatedFields, {enforceUniqueDisplayName: false});
        if (duplicate != null) {
          notifyDuplicateField(duplicate, 'task');
          return;
        }
        try {
          await saveSystemName(section.id, fieldName, newSystemName, allFieldsJson, category);
          setSections((prev) =>
            prev.map((s) => {
              if (s.id !== sectionId) {
                return s;
              }
              const updateFieldInfos = (infos: Array<SchemaFieldInfo>): Array<SchemaFieldInfo> =>
                infos.map((f) => (f.name === fieldName ? {...f, systemName: newSystemName} : f));
              const updateFieldsJson = (fields: Array<SchemaField>): Array<SchemaField> =>
                fields.map((f) => (f.name === fieldName ? {...f, systemName: newSystemName} : f));
              return {
                ...s,
                ...(category === 'input'
                  ? {
                      inputFields: updateFieldInfos(s.inputFields),
                      allInputFieldsJson: updateFieldsJson(s.allInputFieldsJson),
                    }
                  : {
                      outputFields: updateFieldInfos(s.outputFields),
                      allOutputFieldsJson: updateFieldsJson(s.allOutputFieldsJson),
                    }),
              };
            })
          );
          notifications.success('Updated schema successfully');
        } catch (error) {
          notifySchemaUpdateError(error);
          throw error;
        }
      },
    []
  );

  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  return (
    <ColumnWidthProvider>
      <div className={css(styles.container)}>
        {sections.map((section) => (
          <CollapsibleTransitionCard
            key={section.id}
            header={section.name}
            isOpenByDefault={true}
            fullWidthChildren={true}
            dataTestAttrs={{
              component: 'FlowchartSystemNamesTab',
              element: 'schemaAccordion',
              key: section.id,
            }}
          >
            {section.inputFields.length === 0 && section.outputFields.length === 0 ? (
              <Text size="md" color="dimmed" p="md">
                {NO_CONFIGURED_FIELDS_MESSAGE}
              </Text>
            ) : (
              <div className={css(styles.tablesContainer)}>
                <FieldsTable
                  fields={section.inputFields}
                  headerLabel="Input Fields"
                  dataTestComponent="FlowchartSystemNamesTab-inputFields"
                  onSystemNameSave={handleSystemNameSave(section.id, 'input')}
                />
                <FieldsTable
                  fields={section.outputFields}
                  headerLabel="Output Fields"
                  dataTestComponent="FlowchartSystemNamesTab-outputFields"
                  onSystemNameSave={handleSystemNameSave(section.id, 'output')}
                />
              </div>
            )}
          </CollapsibleTransitionCard>
        ))}
      </div>
    </ColumnWidthProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ':global': {
      '.mantine-Accordion-control': {
        marginBottom: 0,
      },
    },
  },
  tablesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '0 12px',
  },
  tableWrapper: {
    ':global': {
      '.simpleTable': {
        marginBottom: 0,
      },
    },
  },
  headerCell: {
    fontWeight: 600,
    fontSize: '13px',
  },
  fieldNameCell: {
    overflow: 'hidden',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    fontSize: '14px',
    lineHeight: '16px',
    fontWeight: 400,
    color: Color.GRAY_900,
  },
  systemNameCell: {
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    color: Color.GRAY_600,
    fontFamily: FONT_FAMILY_MONOSPACED,
    fontWeight: 500,
    fontSize: '13px',
    lineHeight: 1,
  },
  systemNameText: {
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
  },
  editingContainer: {
    padding: '2px 0',
  },
  archivedField: {
    opacity: 0.6,
  },
  actionIcon: {
    cursor: 'pointer',
    marginLeft: '12px',
    color: Color.GRAY_500,
    ':hover': {
      color: Color.GRAY_700,
    },
  },
  showArchivedLink: {
    color: BLUE,
    fontSize: '13px',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});
